import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { LanguageService } from '../../../../services/language.service';
import { Course, Section, SectionCreatePayload, SectionUpdatePayload, MathUtils } from '@mira-avant/mtlibs';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { SectionService } from '../../../../services/section.service';
import { LocationService } from '../../../../services/location.service';
import { SectionForm } from '../../../forms/standard/section-form/section-form';
import { SectionTable } from './section-table/section-table';
import { FieldLabelComponent } from '../../forms/field-label/field-label.component';

@Component({
	selector: 'app-section-editor',
	standalone: true,
	imports: [AngularModule, ZorroModule, SectionForm, SectionTable, FieldLabelComponent],
	templateUrl: './section-editor.component.html',
	styleUrl: './section-editor.component.scss',
})
export class SectionEditorComponent implements OnInit {
	@ViewChild(SectionTable) private sectionTableComponent: SectionTable | undefined;
	@Input('course') public course: Course | undefined;

	public editSection: Section | undefined;

	constructor(
		public languageService: LanguageService,
		private sectionService: SectionService,
		private locationService: LocationService
	) {}

	ngOnInit(): void {}

	public async addNewSection(): Promise<void> {
		if (!this.course?.uuid || !this.course.client_uuid) {
			throw new Error('Course information is incomplete');
		}

		const firstLocation = await this.locationService
			.getByClient$(this.course.client_uuid, 0, 1)
			.then(locs => locs[0]);

		if (!firstLocation) {
			throw new Error('No locations available');
		}

		const sectionData: SectionCreatePayload = {
			client_uuid: this.course.client_uuid,
			location_uuid: firstLocation.uuid,
			course_uuid: this.course.uuid,
			section_code: MathUtils.generateShortGuid(),
			name: 'New Section',
			language: this.languageService.spokenLanguage$.value,
			active: true,
			allow_self_rostering: false,
			is_demo: false,
			active_start_date: null,
			active_end_date: null,
			sso_section_id: null,
		};

		const newSection = await this.sectionService.create$(sectionData);
		// Ensure the new section has null dates if they come back undefined
		newSection.active_start_date = newSection.active_start_date || null;
		newSection.active_end_date = newSection.active_end_date || null;

		await this.sectionTableComponent?.reloadCurrentPage();
	}

	public async saveSection($event: SectionUpdatePayload): Promise<void> {
		if (!this.editSection) {
			throw new Error('No section selected!');
		}

		await this.sectionService.update$(this.editSection.uuid, $event);
		this.editSection = undefined;
	}

	public edit(section: Section | undefined): void {
		this.editSection = section;
	}

	public async delete(section: Section): Promise<void> {
		await this.sectionService.delete$(section.uuid);
		await this.sectionTableComponent?.reset();
	}
}
